import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { List, Clock, Layers } from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import { useTranslation } from 'react-i18next';
import { fetchAlerts } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useAutoRefresh } from '../contexts/AutoRefreshContext';
import { format, isToday, isYesterday, formatDistanceToNowStrict } from 'date-fns';

interface Alert {
  id: string;
  timestamp: string;
  source: string;
  rule: {
    id: string;
    level: number;
    description: string;
    groups?: string[];
  };
  agent: {
    id: string;
    name: string;
    ip?: string;
  };
}

function levelToStatus(level: number): string {
  if (level >= 12) return 'critical';
  if (level >= 8) return 'warning';
  return 'ok';
}

function severityDotColor(level: number): string {
  if (level >= 12) return 'bg-red-500';
  if (level >= 8) return 'bg-yellow-500';
  return 'bg-green-500';
}

function TimelineView({ alerts, t }: { alerts: Alert[]; t: (key: string) => string }) {
  const grouped = useMemo(() => {
    const groups: Record<string, Alert[]> = {};
    alerts.forEach((a) => {
      if (!a.timestamp) return;
      const d = new Date(a.timestamp);
      const key = format(d, 'yyyy-MM-dd');
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [alerts]);

  if (alerts.length === 0) {
    return <p className="text-center text-gray-500 dark:text-gray-400 py-12">{t('alerts.noAlerts')}</p>;
  }

  return (
    <div className="space-y-6">
      {grouped.map(([dateKey, dayAlerts]) => {
        const d = new Date(dateKey);
        const label = isToday(d) ? t('alerts.today') : isYesterday(d) ? t('alerts.yesterday') : format(d, 'MMM d, yyyy');
        return (
          <div key={dateKey}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">({dayAlerts.length})</span>
              <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative ml-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-0">
              {dayAlerts.map((alert, i) => {
                const level = alert.rule?.level || 0;
                const status = levelToStatus(level);
                const isCritical = level >= 12;
                return (
                  <div key={alert.id || `${dateKey}-${i}`} className="relative pl-6 pb-4 group">
                    <div className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${severityDotColor(level)} ${isCritical ? 'animate-pulse' : ''}`} />
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">
                            {alert.rule?.description || '-'}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <StatusBadge status={status} />
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${alert.source === 'wazuh' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'}`}>
                              {alert.source || 'unknown'}
                            </span>
                            {alert.agent?.name && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {alert.agent.name}
                              </span>
                            )}
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                              #{alert.rule?.id}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap font-mono">
                          {alert.timestamp ? format(new Date(alert.timestamp), 'HH:mm:ss') : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ageColor(ts: string): string {
  if (!ts) return 'text-gray-400';
  const h = (Date.now() - new Date(ts).getTime()) / 3600000;
  if (h < 1) return 'text-green-600 dark:text-green-400';
  if (h < 4) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

interface GroupedAlert {
  ruleId: string;
  description: string;
  maxLevel: number;
  count: number;
  firstSeen: string;
  lastSeen: string;
  hosts: string[];
  source: string;
  alerts: Alert[];
}

function GroupedView({ alerts, t }: { alerts: Alert[]; t: (key: string) => string }) {
  const groups = useMemo(() => {
    const map: Record<string, GroupedAlert> = {};
    alerts.forEach((a) => {
      const key = a.rule?.id || 'unknown';
      if (!map[key]) {
        map[key] = {
          ruleId: key,
          description: a.rule?.description || '-',
          maxLevel: a.rule?.level || 0,
          count: 0,
          firstSeen: a.timestamp,
          lastSeen: a.timestamp,
          hosts: [],
          source: a.source,
          alerts: [],
        };
      }
      const g = map[key];
      g.count++;
      g.alerts.push(a);
      if (a.rule?.level && a.rule.level > g.maxLevel) g.maxLevel = a.rule.level;
      if (a.timestamp < g.firstSeen) g.firstSeen = a.timestamp;
      if (a.timestamp > g.lastSeen) g.lastSeen = a.timestamp;
      if (a.agent?.name && !g.hosts.includes(a.agent.name)) g.hosts.push(a.agent.name);
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [alerts]);

  if (groups.length === 0) {
    return <p className="text-center text-gray-500 dark:text-gray-400 py-12">{t('alerts.noAlerts')}</p>;
  }

  const columns: Column<GroupedAlert>[] = [
    { key: 'ruleId', header: t('alerts.ruleId'), render: (g) => <span className="font-mono text-xs">{g.ruleId}</span> },
    { key: 'description', header: t('alerts.description'), render: (g) => g.description },
    { key: 'maxLevel', header: t('alerts.level'), render: (g) => <StatusBadge status={levelToStatus(g.maxLevel)} /> },
    { key: 'count', header: t('alerts.occurrences'), render: (g) => (
      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{g.count}</span>
    )},
    { key: 'hosts', header: t('alerts.affectedHosts'), render: (g) => (
      <div className="flex flex-wrap gap-1">
        {g.hosts.slice(0, 3).map((h) => (
          <span key={h} className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{h}</span>
        ))}
        {g.hosts.length > 3 && <span className="text-xs text-gray-400">+{g.hosts.length - 3}</span>}
      </div>
    )},
    { key: 'lastSeen', header: t('alerts.lastSeen'), render: (g) => (
      <span className={`text-xs font-mono ${ageColor(g.lastSeen)}`}>
        {g.lastSeen ? formatDistanceToNowStrict(new Date(g.lastSeen), { addSuffix: true }) : '-'}
      </span>
    )},
  ];

  return (
    <DataTable
      data={groups}
      columns={columns}
      keyExtractor={(g) => g.ruleId}
      expandContent={(g) => (
        <div className="space-y-1 max-h-60 overflow-auto">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {t('alerts.firstSeen')}: {g.firstSeen ? format(new Date(g.firstSeen), 'MMM d, HH:mm:ss') : '-'} &middot;{' '}
            {t('alerts.lastSeen')}: {g.lastSeen ? format(new Date(g.lastSeen), 'MMM d, HH:mm:ss') : '-'}
          </p>
          {g.alerts.map((a, i) => (
            <div key={a.id || i} className="flex items-center gap-3 text-xs py-1 border-b border-gray-100 dark:border-gray-700/30 last:border-0">
              <span className="text-gray-400 dark:text-gray-500 font-mono w-16">
                {a.timestamp ? format(new Date(a.timestamp), 'HH:mm:ss') : '-'}
              </span>
              <span className={`w-2 h-2 rounded-full ${severityDotColor(a.rule?.level || 0)}`} />
              <span className="text-gray-600 dark:text-gray-300">{a.agent?.name || '-'}</span>
              <span className={`ml-auto font-mono ${ageColor(a.timestamp)}`}>
                {a.timestamp ? formatDistanceToNowStrict(new Date(a.timestamp), { addSuffix: true }) : '-'}
              </span>
            </div>
          ))}
        </div>
      )}
      emptyMessage={t('alerts.noAlerts')}
    />
  );
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [sourceFilter, setSourceFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'timeline' | 'grouped'>(() =>
    (localStorage.getItem('secuguard_alerts_view') as 'table' | 'timeline' | 'grouped') || 'table'
  );
  const { selectedTenantId } = useTenant();
  const { t } = useTranslation();
  const { subscribe } = useAutoRefresh();

  const toggleView = (mode: 'table' | 'timeline' | 'grouped') => {
    setViewMode(mode);
    localStorage.setItem('secuguard_alerts_view', mode);
  };

  const loadData = useCallback(() => {
    setLoading(true);
    fetchAlerts({
      hours,
      tenant_id: selectedTenantId || undefined,
      source: sourceFilter || undefined,
      severity: severityFilter || undefined,
    })
      .then((data) => setAlerts(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [hours, selectedTenantId, sourceFilter, severityFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => subscribe(loadData), [subscribe, loadData]);

  const columns: Column<Alert>[] = [
    {
      key: 'timestamp',
      header: t('alerts.time'),
      render: (a) =>
        a.timestamp ? format(new Date(a.timestamp), 'MMM d, HH:mm:ss') : '-',
    },
    {
      key: 'level',
      header: t('alerts.level'),
      render: (a) => <StatusBadge status={levelToStatus(a.rule?.level || 0)} />,
    },
    { key: 'source', header: t('alerts.source'), render: (a) => (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.source === 'wazuh' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'}`}>
        {a.source || 'unknown'}
      </span>
    )},
    { key: 'rule_id', header: t('alerts.ruleId'), render: (a) => a.rule?.id || '-' },
    { key: 'description', header: t('alerts.description'), render: (a) => a.rule?.description || '-' },
    { key: 'agent', header: t('alerts.agentTenant'), render: (a) => a.agent?.name || '-' },
    { key: 'age', header: t('alerts.age'), render: (a) => (
      <span className={`text-xs font-mono ${ageColor(a.timestamp)}`}>
        {a.timestamp ? formatDistanceToNowStrict(new Date(a.timestamp), { addSuffix: true }) : '-'}
      </span>
    )},
  ];

  if (loading) {
    return <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />;
  }

  const selectClass = "text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('alerts.alertCount', { count: alerts.length })}</p>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className={selectClass}>
          <option value={6}>{t('alerts.last6h')}</option>
          <option value={24}>{t('alerts.last24h')}</option>
          <option value={72}>{t('alerts.last3d')}</option>
          <option value={168}>{t('alerts.last7d')}</option>
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className={selectClass}>
          <option value="">{t('alerts.allSeverities')}</option>
          <option value="critical">{t('alerts.critical')}</option>
          <option value="warning">{t('alerts.warning')}</option>
          <option value="ok">{t('alerts.ok')}</option>
        </select>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={selectClass}>
          <option value="">{t('alerts.allSources')}</option>
          <option value="wazuh">Wazuh</option>
          <option value="analyzer">Analyzer</option>
        </select>
        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden ml-auto">
          <button
            onClick={() => toggleView('table')}
            className={`p-1.5 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title={t('alerts.tableView')}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => toggleView('timeline')}
            className={`p-1.5 ${viewMode === 'timeline' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title={t('alerts.timelineView')}
          >
            <Clock className="w-4 h-4" />
          </button>
          <button
            onClick={() => toggleView('grouped')}
            className={`p-1.5 ${viewMode === 'grouped' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            title={t('alerts.groupedView')}
          >
            <Layers className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <DataTable
          data={alerts}
          columns={columns}
          keyExtractor={(a) => a.id || a.rule?.id || Math.random().toString()}
          expandContent={(a) => (
            <div className="text-xs space-y-1">
              <p className="text-gray-700 dark:text-gray-300"><strong>{t('alerts.description')}:</strong> {a.rule?.description || '-'}</p>
              {a.rule?.groups && a.rule.groups.length > 0 && (
                <p className="text-gray-500 dark:text-gray-400"><strong>{t('alerts.groups')}:</strong> {a.rule.groups.join(', ')}</p>
              )}
              {a.agent?.ip && <p className="text-gray-500 dark:text-gray-400"><strong>IP:</strong> {a.agent.ip}</p>}
            </div>
          )}
          emptyMessage={t('alerts.noAlerts')}
        />
      ) : viewMode === 'timeline' ? (
        <TimelineView alerts={alerts} t={t} />
      ) : (
        <GroupedView alerts={alerts} t={t} />
      )}
    </div>
  );
}
